import { DialogCloseEvent } from '@theme/dialog';
import { clamp, preventDefault, isMobileBreakpoint } from '@theme/utilities';
import { Component } from '@theme/component';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
/* 2026-09-26, owner: "zoom mode trên mobiles có thể kéo ảnh main để next sang ảnh
   khác được và phải mượt, dùng sẵn ảnh đang load rồi k cần load thêm".
   The dialog used to open at 1.5x and every one-finger drag panned the picture,
   so there was no way to reach the next photo by dragging it. It now opens at
   1x (the whole photo), where a horizontal drag moves the strip to the next or
   previous photo; a double tap or a pinch zooms, and a zoomed photo pans until
   its edge, where a further pull hands over to the strip. DEFAULT_ZOOM is what a
   double tap zooms TO. */
const OPEN_ZOOM = 1;
const DEFAULT_ZOOM = 2;
const SWIPE_COMMIT_PX = 50;      // a drag this long (or a flick) moves to the next photo
const EDGE_HANDOFF_PX = 60;      // pulling this far past a zoomed photo's edge does the same
const FLICK_PX_PER_MS = 0.35;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement} image - The image element to zoom and drag.
 */

/** @extends {Component<Refs>} */
export class DragZoomWrapper extends Component {
  requiredRefs = ['image'];
  #controller = new AbortController();
  /** @type {number} */
  #scale = OPEN_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = OPEN_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {number} */
  #lastTapTime = 0;
  /** @type {Point | null} */
  #lastTapPosition = null;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  /** @type {boolean} */
  #hasManualZoom = false;

  get #image() {
    return this.refs.image;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.#image) return;

    this.#initResizeListener();
    window.addEventListener(DialogCloseEvent.eventName, this.#resetZoom);

    if (!isMobileBreakpoint()) return;

    this.#initEventListeners();
    this.#reuseGalleryImage();
    this.#updateTransform();
  }

  /* The zoom dialog renders its own <img> at zoom widths, so opening it and
     swiping fetched every photo again (5 new downloads measured on the cutting
     board). The photo in the page's gallery is the same file: copying its
     srcset and sizes makes the browser pick the URL it already has. The zoom
     widths are kept aside and only requested once the shopper actually zooms
     in, and the sharper file replaces the shown one only after it has loaded. */
  #hiRes = null;
  #reuseGalleryImage() {
    const img = this.#image;
    if (!img || img.dataset.hlZoomReuse) return;
    const file = (img.getAttribute('src') || '').split('?')[0].split('/').pop();
    if (!file) return;
    const twin = Array.from(document.querySelectorAll('media-gallery img, .product-media img'))
      .find((i) => i !== img && !i.closest('dialog') && (i.getAttribute('src') || '').split('?')[0].split('/').pop() === file);
    if (!twin || !twin.getAttribute('srcset')) return;
    img.dataset.hlZoomReuse = '1';
    this.#hiRes = { srcset: img.getAttribute('srcset'), sizes: img.getAttribute('sizes'), src: img.getAttribute('src') };
    img.setAttribute('sizes', twin.getAttribute('sizes') || '100vw');
    img.setAttribute('srcset', twin.getAttribute('srcset'));
    img.setAttribute('src', twin.currentSrc || twin.getAttribute('src'));
  }

  #upgradeToHiRes() {
    const hi = this.#hiRes;
    const img = this.#image;
    if (!hi || !img) return;
    this.#hiRes = null;
    /* Sized for the zoom, not for the screen: at 2x a 390px phone shows the
       photo 780 CSS px wide, and the zoom srcset read at `100vw` picked a 1000px
       file — smaller than the 1080 the gallery had already shown. */
    const sizes = `${Math.round((this.clientWidth || window.innerWidth) * Math.max(this.#scale, DEFAULT_ZOOM))}px`;
    const probe = new Image();
    probe.sizes = sizes;
    probe.srcset = hi.srcset;
    probe.src = hi.src;
    const swap = () => {
      // Only ever sharper: a file no larger than the one on screen is dropped.
      // Compared by the width in the URL — naturalWidth is divided by the
      // srcset density, so it cannot compare two different srcsets.
      const px = (u) => +((/[?&]width=(\d+)/.exec(u || '') || [])[1] || 0);
      if (px(probe.currentSrc) <= px(img.currentSrc)) return;
      img.setAttribute('sizes', sizes);
      img.setAttribute('srcset', hi.srcset);
    };
    if (probe.complete) swap();
    else probe.addEventListener('load', swap, { once: true });
  }

  /** The <ul> of photos this wrapper sits in, and its slides. */
  get #strip() {
    return /** @type {HTMLElement | null} */ (this.closest('.dialog-zoomed-gallery'));
  }

  /** @type {{ x: number, y: number, left: number, t: number, axis: string | null } | null} */
  #swipe = null;
  #edgePull = 0;

  #slideIndex(strip, from = strip.scrollLeft) {
    const items = Array.from(strip.children);
    const left = from;
    let best = 0, bestD = Infinity;
    items.forEach((li, i) => { const d = Math.abs(/** @type {HTMLElement} */ (li).offsetLeft - left); if (d < bestD) { bestD = d; best = i; } });
    return { items, index: best };
  }

  /** Move the strip one photo, measured by offsetLeft, not by width. */
  #goTo(strip, dir, from = strip.scrollLeft) {
    const { items, index } = this.#slideIndex(strip, from);
    const next = Math.max(0, Math.min(items.length - 1, index + dir));
    const target = /** @type {HTMLElement} */ (items[next]);
    strip.style.scrollSnapType = '';
    strip.scrollTo({ left: target ? target.offsetLeft : strip.scrollLeft, behavior: 'smooth' });
    if (next !== index) this.#resetZoom();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);

    // Initialize transform immediately
    this.#updateTransform();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    if (!this.#initialized && isMobileBreakpoint()) {
      this.#initEventListeners();
    }

    if (this.#initialized) {
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /* ---- GESTURES, REWRITTEN 2026-10-21 ---------------------------------------
     Owner: "Zoom mode cho phép zoom tự nhiên như iphone zoom bình thường cả android
     nữa, hiện tại zoom cứ bị khó move".

     What made it hard, in the code this replaces:
     - after a pinch, lifting one finger left the other unable to pan (the pinch
       cleared #isDragging and nothing restarted the drag);
     - a pinch could not move the photo: the midpoint was only used to correct the
       scale, incrementally, so the photo drifted out from under the fingers;
     - panning stopped dead at the edge and stopped dead on release — no rubber band,
       no momentum, no spring back.

     The model now is the one a photo viewer uses. The transform is
     scale(s) translate(t) about the image centre, so a point at offset d from the
     centre shows the local point d/s - t. Every gesture records, at its start, the
     local point under the finger (or under the pinch midpoint) and solves for the
     translate that keeps that point under it — absolute, never accumulated, so
     nothing drifts. Past the photo's edge the movement is damped (RUBBER) and
     springs back on release; a release with speed carries on and decays; a pinch
     past 1x or MAX_ZOOM springs back to the limit. A one-finger horizontal drag at
     1x still moves the strip to the next photo, and pulling a zoomed photo well past
     its side edge still hands over to the strip. */

  /** Damping applied to movement past an edge, and to scale past its limits. */
  static RUBBER = 0.35;
  static SNAP_MS = 300;
  static SNAP_EASE = 'cubic-bezier(.2,.8,.2,1)';
  static DOUBLE_TAP_ZOOM = 2.5;

  /** @type {null | { mode: 'pan' | 'pinch', [k: string]: any }} */
  #g = null;
  /** @type {{ t: number, x: number, y: number }[]} */
  #track = [];
  #inertiaFrame = 0;
  #snapTimer = 0;

  #centre() {
    const r = this.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }

  /** How far the photo may move each way at scale s (in translate units). */
  #limits(s = this.#scale) {
    const w = this.clientWidth, h = this.clientHeight, img = this.#image;
    if (!w || !h || !img || s <= MIN_ZOOM) return { x: 0, y: 0 };
    const nw = img.naturalWidth || w, nh = img.naturalHeight || w;
    const fitW = nw / nh > w / h ? w : (h * nw) / nh;
    const fitH = nw / nh > w / h ? (w * nh) / nw : h;
    return {
      x: Math.max(0, fitW * s - w) / 2 / s,
      y: Math.max(0, fitH * s - h) / 2 / s,
    };
  }

  /** v past ±max is damped: the photo follows the finger more slowly beyond its edge. */
  #rubber(v, max) {
    if (v > max) return max + (v - max) * DragZoomWrapper.RUBBER;
    if (v < -max) return -max + (v + max) * DragZoomWrapper.RUBBER;
    return v;
  }

  /** Paint the current scale and translate, optionally as a spring. */
  #paint(animate = false) {
    const img = this.#image;
    if (img) {
      img.style.transition = animate
        ? `transform ${DragZoomWrapper.SNAP_MS}ms ${DragZoomWrapper.SNAP_EASE}`
        : 'none';
      img.style.willChange = this.#scale > MIN_ZOOM + 0.01 || this.#g ? 'transform' : '';
    }
    if (this.#scale > OPEN_ZOOM + 0.05) this.#upgradeToHiRes();
    this.style.setProperty('--drag-zoom-scale', String(this.#scale));
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
    clearTimeout(this.#snapTimer);
    if (animate && img) this.#snapTimer = window.setTimeout(() => { img.style.transition = 'none'; }, DragZoomWrapper.SNAP_MS + 40);
  }

  #stopMotion() {
    if (this.#inertiaFrame) cancelAnimationFrame(this.#inertiaFrame);
    this.#inertiaFrame = 0;
    clearTimeout(this.#snapTimer);
    if (this.#image) this.#image.style.transition = 'none';
  }

  /** Scale and translate brought back inside their limits, as a spring. */
  #settle() {
    const s = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);
    if (s !== this.#scale && this.#g && this.#g.mid) {
      // Spring back about the last pinch midpoint, so the photo does not slide.
      const c = this.#centre(), m = this.#g.mid;
      const L = { x: (m.x - c.x) / this.#scale - this.#translate.x, y: (m.y - c.y) / this.#scale - this.#translate.y };
      this.#scale = s;
      this.#translate = { x: (m.x - c.x) / s - L.x, y: (m.y - c.y) / s - L.y };
    }
    this.#scale = s;
    if (s <= MIN_ZOOM) this.#translate = { x: 0, y: 0 };
    const lim = this.#limits(s);
    this.#translate = { x: clamp(this.#translate.x, -lim.x, lim.x), y: clamp(this.#translate.y, -lim.y, lim.y) };
    this.#paint(true);
  }

  #startPinch(t1, t2) {
    const c = this.#centre();
    const m = { x: (t1.clientX + t2.clientX) / 2, y: (t1.clientY + t2.clientY) / 2 };
    this.#g = {
      mode: 'pinch',
      dist: Math.max(1, getDistance(t1, t2)),
      s0: this.#scale,
      L: { x: (m.x - c.x) / this.#scale - this.#translate.x, y: (m.y - c.y) / this.#scale - this.#translate.y },
      mid: m,
    };
    this.#swipe = null;
  }

  #movePinch(t1, t2) {
    const g = this.#g;
    if (!g) return;
    const c = this.#centre();
    const m = { x: (t1.clientX + t2.clientX) / 2, y: (t1.clientY + t2.clientY) / 2 };
    let s = (g.s0 * getDistance(t1, t2)) / g.dist;
    if (s > MAX_ZOOM) s = MAX_ZOOM + (s - MAX_ZOOM) * DragZoomWrapper.RUBBER;
    if (s < MIN_ZOOM) s = Math.max(0.6, MIN_ZOOM - (MIN_ZOOM - s) * 0.5);
    this.#scale = s;
    g.mid = m;
    // the local point that was under the midpoint stays under it — pinch and two-finger pan in one
    const raw = { x: (m.x - c.x) / s - g.L.x, y: (m.y - c.y) / s - g.L.y };
    const lim = this.#limits(Math.max(s, MIN_ZOOM));
    this.#translate = { x: this.#rubber(raw.x, lim.x), y: this.#rubber(raw.y, lim.y) };
    this.#paint();
  }

  #startPan(touch) {
    this.#g = {
      mode: 'pan',
      x0: touch.clientX,
      y0: touch.clientY,
      // the raw translate this pan starts from: an already-damped position is undone first
      rx: this.#translate.x,
      ry: this.#translate.y,
      moved: false,
    };
    this.#track = [{ t: performance.now(), x: touch.clientX, y: touch.clientY }];
  }

  #movePan(touch) {
    const g = this.#g;
    if (!g) return;
    const dx = touch.clientX - g.x0, dy = touch.clientY - g.y0;
    if (!g.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    g.moved = true;
    const s = this.#scale, lim = this.#limits(s);
    this.#translate = { x: this.#rubber(g.rx + dx / s, lim.x), y: this.#rubber(g.ry + dy / s, lim.y) };
    const now = performance.now();
    this.#track.push({ t: now, x: touch.clientX, y: touch.clientY });
    while (this.#track.length > 2 && now - this.#track[0].t > 100) this.#track.shift();
    this.#paint();
  }

  /** Release of a pan: hand over to the strip, spring back, or carry on with momentum. */
  #endPan() {
    const s = this.#scale, lim = this.#limits(s);
    const overX = (this.#translate.x - clamp(this.#translate.x, -lim.x, lim.x)) * s;
    const strip = this.#strip;
    if (strip && Math.abs(overX) > EDGE_HANDOFF_PX) {
      this.#goTo(strip, overX < 0 ? 1 : -1);
      return;
    }
    const tr = this.#track, a = tr[0], b = tr[tr.length - 1];
    const dt = b && a ? b.t - a.t : 0;
    const fresh = b && performance.now() - b.t < 60;
    const vx = dt > 0 && fresh ? (b.x - a.x) / dt : 0;   // screen px per ms
    const vy = dt > 0 && fresh ? (b.y - a.y) / dt : 0;
    const outside = overX !== 0 || Math.abs(this.#translate.y) > lim.y + 0.5;
    if (outside || Math.hypot(vx, vy) < 0.08 || s <= MIN_ZOOM) { this.#settle(); return; }
    this.#glide(vx, vy);
  }

  /** Momentum: the photo keeps the release velocity and decays; an edge stops it with a spring. */
  #glide(vx, vy) {
    let last = performance.now();
    const step = (now) => {
      const dt = Math.min(32, now - last);
      last = now;
      const s = this.#scale, lim = this.#limits(s);
      let x = this.#translate.x + (vx * dt) / s;
      let y = this.#translate.y + (vy * dt) / s;
      const decay = Math.pow(0.94, dt / 16);
      vx *= decay; vy *= decay;
      if (Math.abs(x) > lim.x) { x = clamp(x, -lim.x, lim.x); vx = 0; }
      if (Math.abs(y) > lim.y) { y = clamp(y, -lim.y, lim.y); vy = 0; }
      this.#translate = { x, y };
      this.#paint();
      if (Math.hypot(vx, vy) > 0.02) this.#inertiaFrame = requestAnimationFrame(step);
      else this.#inertiaFrame = 0;
    };
    this.#inertiaFrame = requestAnimationFrame(step);
  }

  /** Double tap: zoom in about the tapped point, or back out to the whole photo. */
  #doubleTap(touch) {
    if (this.#scale > MIN_ZOOM + 0.05) {
      this.#scale = MIN_ZOOM;
      this.#translate = { x: 0, y: 0 };
      this.#paint(true);
      return;
    }
    const c = this.#centre();
    const d = { x: touch.clientX - c.x, y: touch.clientY - c.y };
    const L = { x: d.x / this.#scale - this.#translate.x, y: d.y / this.#scale - this.#translate.y };
    const s = Math.min(MAX_ZOOM, DragZoomWrapper.DOUBLE_TAP_ZOOM);
    const lim = this.#limits(s);
    this.#scale = s;
    this.#translate = { x: clamp(d.x / s - L.x, -lim.x, lim.x), y: clamp(d.y / s - L.y, -lim.y, lim.y) };
    this.#paint(true);
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    preventDefault(event);
    this.#stopMotion();
    const touches = event.touches;

    if (touches.length >= 2) {
      const t1 = touches[0], t2 = touches[1];
      if (t1 && t2) this.#startPinch(t1, t2);
      return;
    }
    const touch = touches[0];
    if (!touch) return;

    const now = performance.now();
    if (this.#lastTapPosition && now - this.#lastTapTime < DOUBLE_TAP_DELAY &&
        getDistance(touch, this.#lastTapPosition) < DOUBLE_TAP_DISTANCE) {
      this.#lastTapTime = 0;
      this.#lastTapPosition = null;
      this.#g = null;
      this.#swipe = null;
      this.#doubleTap(touch);
      return;
    }
    this.#storeTapInfo(now, touch);

    const strip = this.#strip;
    this.#swipe = strip
      ? { x: touch.clientX, y: touch.clientY, left: strip.scrollLeft, t: now, axis: null }
      : null;
    this.#startPan(touch);
  };

  /**
   * Store tap information for double-tap detection
   * @param {number} currentTime
   * @param {Touch} touch
   */
  #storeTapInfo(currentTime, touch) {
    this.#lastTapTime = currentTime;
    this.#lastTapPosition = { x: touch.clientX, y: touch.clientY };
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    preventDefault(event);
    const touches = event.touches;

    if (touches.length >= 2) {
      const t1 = touches[0], t2 = touches[1];
      if (!t1 || !t2) return;
      if (!this.#g || this.#g.mode !== 'pinch') this.#startPinch(t1, t2);
      this.#movePinch(t1, t2);
      this.#lastTapPosition = null;
      return;
    }
    const touch = touches[0];
    if (!touch || !this.#g || this.#g.mode !== 'pan') return;

    // Whole photo on screen: a horizontal drag carries the strip with the finger.
    const strip = this.#strip;
    const sw = this.#swipe;
    if (strip && sw && this.#scale <= OPEN_ZOOM + 0.01) {
      const dx = touch.clientX - sw.x, dy = touch.clientY - sw.y;
      if (!sw.axis && Math.hypot(dx, dy) > DRAG_THRESHOLD) sw.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (sw.axis === 'x') {
        strip.style.scrollSnapType = 'none';
        strip.scrollLeft = sw.left - dx;
      }
      return;
    }
    this.#movePan(touch);
    if (this.#g && this.#g.moved) this.#lastTapPosition = null;
  };

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    const left = event.touches;

    // Two fingers down to one: the remaining finger pans on from where the photo is.
    if (left.length === 1) {
      const t = left[0];
      if (t) {
        const s = this.#scale;
        if (s < MIN_ZOOM || s > MAX_ZOOM) this.#settle();
        this.#swipe = null;
        this.#startPan(t);
      }
      return;
    }
    if (left.length > 1) return;

    const g = this.#g;
    const strip = this.#strip;
    const sw = this.#swipe;
    if (g && g.mode === 'pan' && strip && sw && this.#scale <= OPEN_ZOOM + 0.01 && sw.axis === 'x') {
      const touch = event.changedTouches && event.changedTouches[0];
      const dx = touch ? touch.clientX - sw.x : 0;
      const v = Math.abs(dx) / Math.max(1, performance.now() - sw.t);
      const dir = (Math.abs(dx) > SWIPE_COMMIT_PX || v > FLICK_PX_PER_MS) ? (dx < 0 ? 1 : -1) : 0;
      // Counted from the photo the drag started on, and scrolled there from
      // wherever the finger left it, so there is no jump back first.
      this.#goTo(strip, dir, sw.left);
    } else if (g && g.mode === 'pan' && g.moved) {
      this.#endPan();
    } else {
      this.#settle();
    }
    this.#g = null;
    this.#swipe = null;
    this.#isDragging = false;
    this.#hasDraggedBeyondThreshold = false;
  };

  /**
   * Constrain image translation to keep it within the viewport
   */
  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight || !this.#image) return;

    // Keep scale between MIN_ZOOM (1) and MAX_ZOOM (5)
    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    // At minimum zoom (1x), the full image should be visible with no dragging allowed
    if (this.#scale <= MIN_ZOOM) {
      this.#translate.x = 0;
      this.#translate.y = 0;
      return;
    }

    // Get wrapper dimensions
    const wrapperRect = this.getBoundingClientRect();

    // Calculate ACTUAL image content dimensions at current zoom
    // The image element may fill the wrapper, but the content has its own aspect ratio
    const imageElement = this.#image;
    let naturalWidth, naturalHeight;

    // Try to get natural dimensions
    if (imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
      naturalWidth = imageElement.naturalWidth;
      naturalHeight = imageElement.naturalHeight;
    } else {
      // Fallback: assume square image if we can't get natural dimensions
      naturalWidth = wrapperRect.width;
      naturalHeight = wrapperRect.width;
    }

    // Calculate how the image fits within the wrapper (object-fit: contain behavior)
    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;

    let actualImageWidth, actualImageHeight;

    if (imageAspectRatio > wrapperAspectRatio) {
      // Image is wider - width fits exactly, height is smaller (letterboxed top/bottom)
      actualImageWidth = wrapperRect.width;
      actualImageHeight = actualImageWidth / imageAspectRatio;
    } else {
      // Image is taller - height fits exactly, width is smaller (letterboxed left/right)
      actualImageHeight = wrapperRect.height;
      actualImageWidth = actualImageHeight * imageAspectRatio;
    }

    // Apply current zoom scale
    const scaledImageWidth = actualImageWidth * this.#scale;
    const scaledImageHeight = actualImageHeight * this.#scale;

    // SIMPLE APPROACH: Calculate constraints directly from image content dimensions
    // If image content is larger than wrapper, calculate max translation directly

    const horizontalOverflow = Math.max(0, scaledImageWidth - wrapperRect.width);
    const verticalOverflow = Math.max(0, scaledImageHeight - wrapperRect.height);

    // Max translation is half the overflow (since image starts centered)
    const maxTranslateX = horizontalOverflow / 2 / this.#scale;
    const maxTranslateY = verticalOverflow / 2 / this.#scale;

    // Apply symmetric constraints (object-fit: contain behavior)
    // Image starts centered, can move maxTranslate in each direction
    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);

    // Apply final transforms to CSS
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  }

  /**
   * Request an animation frame to update the transform
   */
  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  /**
   * Cancel any pending animation frame
   */
  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;
    if (this.#scale > OPEN_ZOOM + 0.05) this.#upgradeToHiRes();

    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  };

  /**
   * Reset zoom to default state (1.5x scale, centered position)
   * Called when zoom is exited/closed
   */
  #resetZoom = () => {
    this.#stopMotion();
    this.#g = null;
    // Reset scale and translation to defaults
    this.#scale = OPEN_ZOOM;
    this.#startScale = OPEN_ZOOM;
    this.#translate.x = 0;
    this.#translate.y = 0;

    // Reset gesture state to prevent interference on next zoom open
    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#hasDraggedBeyondThreshold = false;

    // Update CSS properties to reflect reset state
    this.style.setProperty('--drag-zoom-scale', OPEN_ZOOM.toString());
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');
  };

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

/**
 * Calculate distance between two points or touches
 * @param {Point | Touch} point1 - First point or touch
 * @param {Point | Touch} point2 - Second point or touch
 * @returns {number} Distance between the points
 */
function getDistance(point1, point2) {
  // Handle both Point objects (x, y) and Touch objects (clientX, clientY)
  const x1 = /** @type {Point} */ (point1).x ?? /** @type {Touch} */ (point1).clientX;
  const y1 = /** @type {Point} */ (point1).y ?? /** @type {Touch} */ (point1).clientY;
  const x2 = /** @type {Point} */ (point2).x ?? /** @type {Touch} */ (point2).clientX;
  const y2 = /** @type {Point} */ (point2).y ?? /** @type {Touch} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {HTMLElement} ZoomDialogElement
 * @property {Function} close - Method to close the zoom dialog
 */
